import { Box, Typography, Button } from "@mui/material";
import EmptyState from "./EmptyState";

/**
 * AttentionQueue — Phase 6 (Design Gap Audit item F.4) reusable component.
 *
 * Action-oriented list matching the prototype's "Needs attention" card.
 *
 * SCOPE NOTE (reported honestly): the prototype's example items include
 * "SLA breach", "stalled candidates", "awaiting client feedback",
 * "interview today", and "duplicates". Only SOME of these are reliably
 * derivable from data already loaded by Dashboard.jsx:
 *   - SLA breach (old positions with zero candidates) — YES, derivable
 *     from existing job dates + candidate counts.
 *   - Duplicates (candidates marked feedback=DUPLICATE) — YES, derivable.
 *   - "Stalled candidates" (stuck N days in one stage) and "awaiting
 *     client feedback" — NOT reliably derivable: the Candidate entity has
 *     no per-stage-transition timestamp, only one overall updatedAt, so
 *     there's no way to know how long a candidate has sat in their
 *     CURRENT stage specifically versus when the whole record last
 *     changed for any reason.
 *   - "Interview today" — technically derivable from the Interview table
 *     (via the My Day feature's API), but Dashboard.jsx does not currently
 *     call that endpoint; adding it is a reasonable future enhancement,
 *     not done in this pass to keep this phase's surface area contained.
 * Per "only show conditions that can be determined from existing data" /
 * "do not invent backend operations", the two reliable items above are
 * implemented; the others are intentionally left out rather than faked.
 *
 * Props:
 *   items - array of { id, label, description, actionLabel, onAction } (required, may be empty)
 */
export default function AttentionQueue({ items }) {
  if (!items || items.length === 0) {
    return (
      <EmptyState
        title="Nothing needs attention"
        description="No SLA breaches or duplicate candidates right now."
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {items.map((item) => (
        <Box
          key={item.id}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            p: 1.25,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {item.label}
            </Typography>
            {item.description && (
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {item.description}
              </Typography>
            )}
          </Box>
          {item.actionLabel && (
            <Button
              size="small"
              variant="outlined"
              onClick={item.onAction}
              sx={{ flexShrink: 0, height: 28, fontSize: 12 }}
            >
              {item.actionLabel}
            </Button>
          )}
        </Box>
      ))}
    </Box>
  );
}
